import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mic, MessageSquare, Search, Brain, Database, ArrowDown } from 'lucide-react';

const stepIcons = [Phone, Mic, MessageSquare, Search, Brain, Database];

export default function FlowchartSection() {
  const { t } = useLanguage();
  const [activeStep, setActiveStep] = useState<number | null>(null);

  return (
    <section id="how-it-works" className="section-padding">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-semibold mb-4">
            {t.flowchart.badge}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t.flowchart.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.flowchart.subtitle}
          </p>
        </motion.div>

        {/* Interactive Flowchart */}
        <div className="max-w-3xl mx-auto mb-16">
          {t.flowchart.steps.map((step, i) => {
            const Icon = stepIcons[i];
            const isActive = activeStep === i;
            return (
              <div key={i}>
                <motion.div
                  initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => setActiveStep(isActive ? null : i)}
                  className={`flowchart-node ${isActive ? 'active' : ''} flex items-start gap-4`}
                >
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                    isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-muted-foreground">{String(i + 1).padStart(2, '0')}</span>
                      <h3 className="font-semibold text-foreground">{step.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                    <AnimatePresence>
                      {isActive && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-3 pt-3 border-t border-border"
                        >
                          <p className="text-sm text-foreground/80">{step.detail}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
                {i < t.flowchart.steps.length - 1 && (
                  <div className="flex justify-center py-2">
                    <ArrowDown size={18} className="text-border" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Powered By */}
        <p className="text-center text-sm font-medium text-muted-foreground mb-12">
          {t.flowchart.poweredBy}
        </p>
      </div>
    </section>
  );
}
